import {
  type NotifyPayload,
  type PlatformCapabilities,
  PlatformError,
} from './types.js';

/**
 * WebPlatform — UI/UX demo + spec lab stub (ADR-019, scope expanded by ADR-021).
 *
 * iOS / Android native 端各自直接呼平台 API（SwiftUI / Compose），不經此 abstraction。
 * Web 端僅作為 spec demo，敏感 API 用 stub 撐畫面避免權限提示。
 */
export class WebPlatform implements PlatformCapabilities {
  readonly platformName = 'web-demo' as const;

  async notify(payload: NotifyPayload): Promise<void> {
    console.info('[WebPlatform.notify]', payload);
    dispatchInAppBanner(payload);
  }

  async scanBarcode(): Promise<string> {
    throw new PlatformError(
      'unsupported_on_web',
      'Barcode scanning is only available in the native apps. Web demo may gain BarcodeDetector support later.'
    );
  }

  async pickPhoto(): Promise<Blob> {
    return await pickFile('image/*');
  }

  async shareFile(blob: Blob, name: string): Promise<void> {
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      const file = new File([blob], name, { type: blob.type || 'application/octet-stream' });
      try {
        await navigator.share({ files: [file], title: name });
        return;
      } catch (err) {
        if ((err as DOMException)?.name === 'AbortError') {
          throw new PlatformError('cancelled', 'User cancelled share.');
        }
        // fall through to download fallback
      }
    }
    triggerDownload(blob, name);
  }
}

function dispatchInAppBanner(payload: NotifyPayload): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('mlh:notify', { detail: payload }));
}

function pickFile(accept: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new PlatformError('unknown', 'document is unavailable'));
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new PlatformError('cancelled', 'No file selected.'));
        return;
      }
      resolve(file);
    };
    input.click();
  });
}

function triggerDownload(blob: Blob, name: string): void {
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
